import { useState, useCallback, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Reorder, AnimatePresence, motion } from 'motion/react';
import { Check } from 'lucide-react';
import { useDockStore } from './useDockStore';
import { DockItem } from './DockItem';
import { DockAddButton } from './DockAddButton';
import { AppStoreDrawer } from './AppStoreDrawer';
import { bloomContainerVariants, bloomItemVariants } from './dockAnimations';
import { haptic } from '@/utils/haptics';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useEditModeStore } from '../useEditModeStore';

interface SmartDockProps {
  onAction?: (action: string) => void;
}

export function SmartDock({ onAction }: SmartDockProps = {}) {
  const { t } = useTranslation();
  const { activeApps, availableApps, isFull, reorder, addApp, removeApp } = useDockStore();
  const { isEditMode: editMode, enter: enterEditGlobal, exit: exitEditGlobal } = useEditModeStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dockHovered, setDockHovered] = useState(false);
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const ids = activeApps.map((a) => a.id);

  const enterEditMode = useCallback(() => {
    if (editMode) return;
    enterEditGlobal();
    haptic.heavy();
  }, [editMode, enterEditGlobal]);

  const exitEditMode = useCallback(() => {
    exitEditGlobal();
  }, [exitEditGlobal]);

  // Long-press detection for mobile
  const handlePointerDown = useCallback(() => {
    if (!isMobile) return;
    longPressTimer.current = setTimeout(enterEditMode, 500);
  }, [isMobile, enterEditMode]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Exit edit mode on outside click (but not when clicking inside widget edit zone)
  useEffect(() => {
    if (!editMode) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (containerRef.current && !containerRef.current.contains(target) && !target.closest('[data-edit-zone]')) {
        exitEditMode();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editMode, exitEditMode]);

  const handleRemove = useCallback(
    (id: string) => {
      removeApp(id);
    },
    [removeApp],
  );

  const handleAdd = useCallback(
    (id: string) => {
      addApp(id);
    },
    [addApp],
  );

  return (
    <>
      <motion.div
        ref={containerRef}
        className="-mt-[70px] relative z-30 smart-dock"
        variants={bloomContainerVariants}
        initial="hidden"
        animate="visible"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={!isMobile ? () => setDockHovered(true) : undefined}
        onMouseLeave={!isMobile ? () => setDockHovered(false) : undefined}
      >
        {/* Mobile done button */}
        {isMobile && editMode && (
          <div className="flex justify-end px-4 mb-1">
            <motion.button
              className="flex items-center gap-1 rounded-full bg-white/60 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-primary-700 shadow-sm"
              onClick={exitEditMode}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Check size={12} />
              {t('home.dock.done')}
            </motion.button>
          </div>
        )}

        <div className="overflow-x-auto hide-scrollbar pt-4 pb-4">
          <Reorder.Group
            axis="x"
            values={ids}
            onReorder={reorder}
            className="flex gap-4 px-4 w-max mx-auto"
            style={{ touchAction: editMode ? 'none' : 'pan-x' }}
          >
            <AnimatePresence mode="popLayout">
              {activeApps.map((app) => (
                <Reorder.Item
                  key={app.id}
                  value={app.id}
                  dragListener={editMode}
                  layout
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  style={{ listStyle: 'none' }}
                >
                  <motion.div variants={bloomItemVariants} initial="hidden" animate="visible">
                    <DockItem
                      app={app}
                      editMode={editMode}
                      onRemove={handleRemove}
                      onEnterEdit={enterEditMode}
                      onAction={onAction}
                      isMobile={isMobile}
                    />
                  </motion.div>
                </Reorder.Item>
              ))}
            </AnimatePresence>

            {/* Add button — visible in edit mode when not full */}
            {editMode && availableApps.length > 0 && !isFull && (
              <DockAddButton onClick={() => setDrawerOpen(true)} isMobile={isMobile} />
            )}

            {/* Ghost "+" — desktop hover hint */}
            {!isMobile && !editMode && availableApps.length > 0 && !isFull && (
              <li style={{ listStyle: 'none' }}>
                <motion.button
                  className="flex flex-shrink-0 flex-col items-center gap-1 cursor-pointer"
                  style={{ width: 90 }}
                  onClick={() => setDrawerOpen(true)}
                  initial={{ opacity: 0, scale: 0.8, filter: 'blur(4px)' }}
                  animate={dockHovered
                    ? { opacity: 0.5, scale: 1, filter: 'blur(0px)' }
                    : { opacity: 0, scale: 0.8, filter: 'blur(4px)' }
                  }
                  whileHover={{ opacity: 0.8, scale: 1.05 }}
                  transition={{ duration: 0.25 }}
                >
                  <div
                    className="flex items-center justify-center border-2 border-dashed border-gray-300/50 bg-white/15"
                    style={{ width: 80, height: 80, borderRadius: '50%' }}
                  >
                    <Plus size={28} className="text-gray-400/60" strokeWidth={1.5} />
                  </div>
                </motion.button>
              </li>
            )}

            {/* Desktop "Done" button in edit mode */}
            {!isMobile && editMode && (
              <li style={{ listStyle: 'none' }} className="flex items-center">
                <motion.button
                  className="flex items-center gap-1 rounded-full bg-white/60 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-primary-700 shadow-sm cursor-pointer"
                  onClick={exitEditMode}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Check size={12} />
                  {t('home.dock.done')}
                </motion.button>
              </li>
            )}
          </Reorder.Group>
        </div>
      </motion.div>

      <AppStoreDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        apps={availableApps}
        onAdd={handleAdd}
      />
    </>
  );
}
